// Which public portals show a published listing, and its link on each.
// Evaluated against the live `portals` rows with the portals' own detail gate
// (propertyMatchesPortal in portal/lib/portal-queries.ts + the composite rule
// in portal/lib/filter-strategies.ts), so a new portal or a changed threshold
// shows up without a release, and no link points at a page that 404s.
// Mirror: mobile/utils/portal-links.ts — keep both identical.

import { videoLinkProvider } from './video-link';

export type PortalRow = {
  slug: string;
  name: string;
  domain: string | null;
  canonical_domain: string | null;
  is_active: boolean | null;
  sort_order: number | null;
  filter_type: string | null;
  filter_value: string | null;
};

/** Columns to select from `portals` for portalLinks(). */
export const PORTAL_LINK_COLUMNS = 'slug, name, domain, canonical_domain, is_active, sort_order, filter_type, filter_value';

export type PortalLinkProperty = {
  id: string;
  ii_id?: string | null;
  property_type_id?: number | string | null;
  department_id?: number | string | null;
  operation?: string | null;
  price?: number | string | null;
  video_url?: string | null;
};

export type PortalLinkContext = {
  /** Medals held by the listing's agency; null when unknown (another agency's listing). */
  agencyMedals: number[] | null;
  /** Slug of the listing's active Sub-Sector (L4): the proyectos portal links its proyecto page. */
  sectorSlug: string | null;
};

export type PortalLink = { slug: string; name: string; host: string; url: string };

/** Map of proyectos: links the listing's proyecto page instead of a listing page. */
const PROYECTOS_PORTAL = 'proyectosbienesraices';

type CompositeSpec = {
  property_type_ids?: number[];
  exclude_ii_ids?: string[];
  min_price_sale?: number | null;
  min_price_rent?: number | null;
};

function parseComposite(raw: string | null): CompositeSpec | null {
  if (!raw) return null;
  try {
    const spec = JSON.parse(raw) as CompositeSpec;
    return spec && typeof spec === 'object' ? spec : null;
  } catch {
    return null;
  }
}

function matchesComposite(p: PortalLinkProperty, spec: CompositeSpec): boolean {
  if (spec.exclude_ii_ids?.length && p.ii_id) {
    const banned = spec.exclude_ii_ids.map((v) => String(v).trim().toUpperCase());
    if (banned.includes(String(p.ii_id).toUpperCase())) return false;
  }
  if (spec.property_type_ids?.length && !spec.property_type_ids.map(Number).includes(Number(p.property_type_id))) {
    return false;
  }
  const price = Number(p.price);
  if (p.price == null || !Number.isFinite(price)) return false;
  const floor = p.operation === 'rent' ? spec.min_price_rent : spec.min_price_sale;
  return floor == null || price >= floor;
}

/** Does the portal's inventory charter include this listing? Unknown filter types say no. */
function inCharter(p: PortalLinkProperty, portal: PortalRow, ctx: PortalLinkContext): boolean {
  if (portal.filter_type === 'has_video') return videoLinkProvider(p.video_url) !== null;
  if (!portal.filter_value) return true;
  switch (portal.filter_type) {
    case 'none':
      return true;
    case 'property_type':
      return Number(p.property_type_id) === Number(portal.filter_value);
    case 'department':
      return Number(p.department_id) === Number(portal.filter_value);
    case 'agency_medal':
      return !!ctx.agencyMedals && ctx.agencyMedals.map(Number).includes(Number(portal.filter_value));
    case 'composite': {
      const spec = parseComposite(portal.filter_value);
      return spec ? matchesComposite(p, spec) : true;
    }
    default:
      return false;
  }
}

function portalBase(portal: PortalRow): string | null {
  const raw = (portal.canonical_domain || (portal.domain ? `https://${portal.domain}` : '')).trim().replace(/\/+$/, '');
  return /^https?:\/\/[^/]+$/.test(raw) ? raw : null;
}

/**
 * The portals a PUBLISHED listing appears on, in portal order, each with the
 * listing's URL there. The caller checks publish_status / archived first.
 */
export function portalLinks(p: PortalLinkProperty, portals: PortalRow[], ctx: PortalLinkContext): PortalLink[] {
  const listingKey = p.ii_id || p.id;
  return [...portals]
    .filter((portal) => portal.is_active !== false)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .flatMap((portal) => {
      const base = portalBase(portal);
      if (!base || !inCharter(p, portal, ctx)) return [];
      let path: string | null;
      if (portal.filter_type === 'has_video') path = p.ii_id ? `/video/${p.ii_id}/` : null; // redirects to the watch slug
      else if (portal.slug === PROYECTOS_PORTAL) path = ctx.sectorSlug ? `/${ctx.sectorSlug}/` : null;
      else path = listingKey ? `/propiedades/${listingKey}` : null;
      if (!path) return [];
      return [{ slug: portal.slug, name: portal.name, host: base.replace(/^https?:\/\//, ''), url: `${base}${path}` }];
    });
}
